import { DialogsRenderer } from '../components/widgets/dialog';
import { NotificationToasterRenderer } from '../components/widgets/notificationToaster';
import React, { FunctionComponent, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import { GetMenuItemIcon } from 'src/components/widgets/leftAppSidebar/IconManager';
import {
    APP_SIDEBAR_WIDTH,
    APP_SIDEBAR_COLLAPSED_WIDTH,
    Body,
    CollapsedNavigation,
    CollapsedNavigationLink,
    ContainerLeft,
    ContainerLeftBottom,
    ContainerLeftTop,
    Header,
    HeaderActions,
    HeaderCollapseButton,
    HeaderLogoLink,
    Main,
    SidebarContent
} from 'src/components/widgets/leftAppSidebar/leftAppSidebarStyles';
import { ItemMenu } from 'src/components/widgets/leftAppSidebar/Menu';
import { AdministratorSidebarFooter } from 'src/components/widgets/leftAppSidebar/SidebarFooter';
import SidebarMenuItems from 'src/components/widgets/leftAppSidebar/SidebarMenuItems';
import { Icon } from 'src/components/widgets/icon';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import { backofficeUrl, getPathName, T } from 'src/shared/helpers';
import MenuOpenIcon from 'src/shared/material_icons/navigation/menu_open.svg';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import styled, { ThemeProvider } from 'styled-components';
import ErrorPageBoundary from '../components/pages/_error';
import OverlayLoading from '../components/widgets/overlayLoading';
import { AppStateProvider } from '../shared/appState';
import { scrollbarsStyle } from '../shared/globals';
import theme from '../shared/theme';
import { administratorLayoutContainerQuery } from './__generated__/administratorLayoutContainerQuery.graphql';

const Container = styled.div`
    font-size: 14px;
    font-family: ${(props) => props.theme.app.font};
    color: ${(props) => props.theme.app.primary};
    min-height: 100%;
    padding: 0px;

    ${scrollbarsStyle}
`;

const AdminShell = styled.div`
    min-height: 100vh;
`;

const AdminContent = styled.div<{ $isCollapsed: boolean }>`
    min-height: 100vh;
    margin-left: ${(props) => (props.$isCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH)}px;
    padding: 0 48px 55px;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    transition: margin-left 0.2s ease;
`;

const COLLAPSED_STORAGE_KEY = 'administrator-sidebar-collapsed';

const Sidebar = ({ isCollapsed, onCollapsedChange }: { isCollapsed: boolean; onCollapsedChange: (collapsed: boolean) => void }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const query = useLazyLoadQuery<administratorLayoutContainerQuery>(
        graphql`
            query administratorLayoutContainerQuery {
                treeMenu
                user {
                    name
                    email
                }
            }
        `,
        {}
    );
    const rootMenu = JSON.parse(query.treeMenu) as ItemMenu;
    const [currentMenu, setCurrentMenu] = useState(rootMenu);
    const adminHomeUrl = backofficeUrl('/console');
    const collapseLabel = isCollapsed ? T._('Expand menu', 'Admin') : T._('Collapse menu', 'Admin');
    const menuItems = rootMenu.menu.filter((item) => !item.isHidden && item.name.toLowerCase() !== 'home');

    const isMenuItemSelected = (item: ItemMenu) => {
        const currentPath = `${location.pathname}${location.search}`.toLowerCase().replace(/\/$/, '');
        const itemPath = getPathName(item.urlPage).toLowerCase().replace(/\/$/, '');
        return currentPath === itemPath || (!!item.regex && new RegExp(item.regex, 'i').test(currentPath));
    };

    return (
        <Main id='main-left-sidebar' $isCollapsed={isCollapsed}>
            <SidebarContent $isCollapsed={isCollapsed}>
                <Header $isCollapsed={isCollapsed}>
                    {!isCollapsed && (
                        <HeaderLogoLink
                            href={adminHomeUrl}
                            onClick={(event) => {
                                event.preventDefault();
                                setCurrentMenu(rootMenu);
                                navigate(adminHomeUrl);
                            }}
                        >
                            <LogoZakeke />
                        </HeaderLogoLink>
                    )}
                    <HeaderActions>
                        <ZTooltip position='bottom' title={collapseLabel}>
                            <HeaderCollapseButton
                                type='button'
                                aria-label={collapseLabel}
                                $isCollapsed={isCollapsed}
                                onClick={() => onCollapsedChange(!isCollapsed)}
                            >
                                <Icon aria-hidden='true'>
                                    <MenuOpenIcon />
                                </Icon>
                            </HeaderCollapseButton>
                        </ZTooltip>
                    </HeaderActions>
                </Header>
                {isCollapsed ? (
                    <CollapsedNavigation aria-label={T._('Main navigation', 'Admin')}>
                        {menuItems.map((item) => {
                            const MenuIcon = GetMenuItemIcon(item);
                            if (!MenuIcon) return null;
                            return (
                                <ZTooltip key={item.key || item.urlPage} position='right' title={item.name}>
                                    <CollapsedNavigationLink
                                        href={item.urlPage}
                                        aria-label={item.name}
                                        $isSelected={isMenuItemSelected(item)}
                                        onClick={(event) => {
                                            event.preventDefault();
                                            if (item.menu.length > 0) {
                                                setCurrentMenu(item);
                                                onCollapsedChange(false);
                                            } else if (item.isExternalUrl) {
                                                window.open(item.urlPage, '_blank', 'noopener,noreferrer');
                                            } else {
                                                navigate(getPathName(item.urlPage));
                                            }
                                        }}
                                    >
                                        <MenuIcon />
                                    </CollapsedNavigationLink>
                                </ZTooltip>
                            );
                        })}
                    </CollapsedNavigation>
                ) : (
                    <Body>
                        <ContainerLeft>
                            <ContainerLeftTop>
                                <SidebarMenuItems currentMenu={currentMenu} goToMenu={setCurrentMenu} iamUser={false} iamUserPermissions={[]} />
                            </ContainerLeftTop>
                            <ContainerLeftBottom>
                                <AdministratorSidebarFooter name={query.user.name ?? ''} email={query.user.email ?? ''} />
                            </ContainerLeftBottom>
                        </ContainerLeft>
                    </Body>
                )}
                {isCollapsed && <AdministratorSidebarFooter isCollapsed name={query.user.name ?? ''} email={query.user.email ?? ''} />}
            </SidebarContent>
        </Main>
    );
};

export const AdmininistratorLayoutContainer: FunctionComponent<{ className?: string; children?: React.ReactNode }> = ({ className, children }) => {
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
        } catch {
            return false;
        }
    });
    const handleCollapsedChange = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        try {
            localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
        } catch {
            /* The menu still works when storage is unavailable. */
        }
    };
    return (
        <AppStateProvider>
            <ThemeProvider theme={theme}>
                <NotificationToasterRenderer />
                <DialogsRenderer />
                <OverlayLoading />
                <Router>
                    <Container className={className}>
                        <AdminShell>
                            <Sidebar isCollapsed={isCollapsed} onCollapsedChange={handleCollapsedChange} />
                            <AdminContent $isCollapsed={isCollapsed}>
                                <ErrorPageBoundary>
                                    <Suspense fallback={<OverlayLoading forceShow />}>{children}</Suspense>
                                </ErrorPageBoundary>
                            </AdminContent>
                        </AdminShell>
                    </Container>
                </Router>
            </ThemeProvider>
        </AppStateProvider>
    );
};
